import styled from "styled-components";
import { BsShieldCheck } from "react-icons/bs";
import { ButtonComp } from "../..";
import { formatMoney } from "../../../utils/currencyFormat";
import RiskBadge from "../RiskBadge";
import { investorButtonProps } from "../investorButtonStyles";
import { toast } from "react-hot-toast";

const HeroWrap = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 2rem;
  min-height: 320px;
  display: flex;
  align-items: flex-end;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  background: #2a2a2a;

  @media (min-width: 768px) {
    min-height: 400px;
  }

  .hero-bg {
    position: absolute;
    inset: 0;
    background-color: #2a2a2a;
    background-size: cover;
    background-position: center;
    transform: scale(1.02);
  }

  /* Always-on readability scrim — works on light or busy cover images */
  .hero-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      linear-gradient(
        180deg,
        rgba(15, 15, 18, 0.45) 0%,
        rgba(15, 15, 18, 0.25) 32%,
        rgba(15, 15, 18, 0.72) 68%,
        rgba(15, 15, 18, 0.92) 100%
      ),
      ${(p) =>
        p.$gradient ||
        "linear-gradient(135deg, rgba(139, 49, 58, 0.55) 0%, rgba(49, 119, 140, 0.4) 100%)"};
  }

  .hero-content {
    position: relative;
    z-index: 1;
    width: 100%;
    padding: 1.5rem;
    color: #fff;

    @media (min-width: 768px) {
      padding: 2.5rem;
    }
  }

  .hero-title {
    font-size: clamp(1.5rem, 4vw, 2.25rem);
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 0.75rem;
    color: #fff;
    text-shadow: 0 2px 16px rgba(0, 0, 0, 0.45);
  }

  .hero-metrics {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin: 1.5rem 0;

    @media (min-width: 576px) {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .metric-pill {
    background: rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 12px;
    padding: 0.75rem 1rem;

    .label {
      font-size: 0.7rem;
      opacity: 0.9;
      margin-bottom: 0.2rem;
    }

    .value {
      font-size: 1rem;
      font-weight: 600;
      color: #fff;
    }
  }

  .hero-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
  }

  .icon-btn-wrap {
    flex: 0 0 auto;
  }
`;

export default function InvestmentDetailHeroPremium({
  opportunity,
  onInvest,
  canInvest = true,
  unavailableNote = "",
}) {
  if (!opportunity) return null;

  const placeholderAction = (label) => () => toast(`${label} — coming soon in demo`);

  const metrics = [
    {
      label: "Expected return",
      value: `${Number(opportunity.expectedReturnPct) || 0}% p.a.`,
    },
    {
      label: "Duration",
      value: opportunity.durationMonths ? `${opportunity.durationMonths} months` : "—",
    },
    {
      label: "Unit price",
      value: formatMoney(Number(opportunity.unitPriceNgn) || 0) || "—",
    },
    {
      label: "Available units",
      value: (Number(opportunity.availableUnits) || 0).toLocaleString(),
    },
  ];

  return (
    <HeroWrap
      role="banner"
      aria-label={`${opportunity.name} investment overview`}
      $gradient={opportunity.heroGradient}
    >
      <div
        className="hero-bg"
        style={
          opportunity.coverImage
            ? { backgroundImage: `url(${opportunity.coverImage})` }
            : undefined
        }
        role="img"
        aria-label={`${opportunity.category || "Investment"} cover`}
      />
      <div className="hero-overlay" aria-hidden />

      <div className="hero-content">
        <div className="d-flex flex-wrap gap-2 mb-3">
          {opportunity.category ? (
            <span
              className="badge rounded-pill px-3 py-2"
              style={{ background: "rgba(255,255,255,0.22)", fontWeight: 600 }}
            >
              {opportunity.category}
            </span>
          ) : null}
          {opportunity.isVerified ? (
            <span
              className="badge rounded-pill px-3 py-2 d-inline-flex align-items-center gap-1"
              style={{ background: "#19bc31", fontWeight: 600 }}
            >
              <BsShieldCheck aria-hidden /> Verified opportunity
            </span>
          ) : null}
          <RiskBadge level={opportunity.riskLevel} />
        </div>

        <h1 className="hero-title">{opportunity.name}</h1>
        {opportunity.marketingDescription ? (
          <p className="mb-2" style={{ maxWidth: "36rem", lineHeight: 1.6, color: "rgba(255,255,255,0.92)" }}>
            {opportunity.marketingDescription}
          </p>
        ) : null}
        <p className="small mb-0" style={{ color: "rgba(255,255,255,0.8)" }}>
          Managed by {opportunity.providerName || "—"}
        </p>

        <div className="hero-metrics">
          {metrics.map((m) => (
            <div key={m.label} className="metric-pill">
              <div className="label">{m.label}</div>
              <div className="value">{m.value}</div>
            </div>
          ))}
        </div>

        <div className="hero-actions d-lg-none">
          {canInvest ? (
            <div style={{ flex: "1 1 160px", maxWidth: 240 }}>
              <ButtonComp
                btnText="Invest now"
                onClick={onInvest}
                BorderColor="#fff"
                ButtonInlineStyle={{
                  backgroundColor: "#fff",
                  color: "var(--color-primary)",
                  borderRadius: 10,
                  minHeight: 48,
                  fontWeight: 600,
                  width: "100%",
                }}
              />
            </div>
          ) : unavailableNote ? (
            <p className="small mb-0" style={{ color: "rgba(255,255,255,0.9)", maxWidth: "20rem" }}>
              {unavailableNote}
            </p>
          ) : null}
          <div className="icon-btn-wrap">
            <ButtonComp
              btnText="Share"
              onClick={placeholderAction("Share")}
              {...investorButtonProps.secondaryInline}
              ButtonInlineStyle={{
                ...investorButtonProps.secondaryInline.ButtonInlineStyle,
                backgroundColor: "rgba(255,255,255,0.15)",
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
              }}
            />
          </div>
          <div className="icon-btn-wrap">
            <ButtonComp
              btnText="Save"
              onClick={placeholderAction("Bookmark")}
              {...investorButtonProps.secondaryInline}
              ButtonInlineStyle={{
                ...investorButtonProps.secondaryInline.ButtonInlineStyle,
                backgroundColor: "rgba(255,255,255,0.15)",
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
              }}
            />
          </div>
        </div>
      </div>
    </HeroWrap>
  );
}

export function InvestmentHeroSecondaryActions({
  onInvest,
  canInvest = true,
  unavailableNote = "",
}) {
  const placeholderAction = (label) => () => toast(`${label} — coming soon in demo`);

  return (
    <div className="d-none d-lg-flex gap-2 mb-4 align-items-center flex-wrap">
      {canInvest ? (
        <div style={{ minWidth: 180 }}>
          <ButtonComp btnText="Invest now" onClick={onInvest} {...investorButtonProps.primary} />
        </div>
      ) : unavailableNote ? (
        <p className="small text-muted mb-0 me-2" style={{ maxWidth: "28rem" }}>
          {unavailableNote}
        </p>
      ) : null}
      <ButtonComp
        btnText="Share"
        onClick={placeholderAction("Share")}
        {...investorButtonProps.outline}
        ButtonInlineStyle={{
          ...investorButtonProps.outline.ButtonInlineStyle,
          width: "auto",
          minWidth: 100,
          maxWidth: 120,
        }}
      />
      <ButtonComp
        btnText="Save"
        onClick={placeholderAction("Bookmark")}
        {...investorButtonProps.outline}
        ButtonInlineStyle={{
          ...investorButtonProps.outline.ButtonInlineStyle,
          width: "auto",
          minWidth: 100,
          maxWidth: 120,
        }}
      />
    </div>
  );
}
